import type {
  BillingProviderSubscriptionStatus,
  ExpertBillingInvoice as ApiExpertSubscriptionInvoice,
  ExpertBillingOptionTerms as ApiExpertSubscriptionBillingOption,
  ExpertBillingPlan as ApiExpertSubscriptionPlan,
  ExpertCurrentBillingSubscription as ApiExpertCurrentSubscription,
  ExpertProviderSubscription as ApiExpertProviderSubscription,
  ExpertSubscriptionOverview as ApiExpertSubscriptionOverview,
} from "@shopexperts/legacy-api";

// These aliases keep the UI vocabulary concise while making the schema-inferred
// legacy-api contract the only source of truth for response shapes.
export type ExpertProviderStatus = BillingProviderSubscriptionStatus;
export type ExpertSubscriptionPlan = ApiExpertSubscriptionPlan;
export type ExpertSubscriptionBillingOption =
  ApiExpertSubscriptionBillingOption;
export type ExpertProviderSubscription = ApiExpertProviderSubscription;
export type ExpertSubscriptionInvoice = ApiExpertSubscriptionInvoice;
export type ExpertCurrentSubscription = ApiExpertCurrentSubscription;
export type ExpertSubscriptionOverview = ApiExpertSubscriptionOverview;

export type SubscriptionStatusTone = "success" | "warning" | "neutral" | "info";

export interface ExpertSubscriptionDisplayState {
  planName: string;
  optionLabel: string | null;
  statusLabel: string;
  statusTone: SubscriptionStatusTone;
  statusMessage: string | null;
  amount: string | null;
  cadence: string | null;
  dateLabel: string;
  dateValue: string;
  servicePeriod: string;
  canManage: boolean;
  scheduledTransition: {
    nextPlanName: string;
    optionLabel: string | null;
    amount: string | null;
    cadence: string | null;
    startsAt: string;
  } | null;
}

const manageableStatuses = new Set<ExpertProviderStatus>([
  "trialing",
  "active",
  "past_due",
  "unpaid",
  "paused",
]);

export function formatSubscriptionDate(
  value: string | null | undefined,
): string {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not scheduled";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatSubscriptionAmount(
  amountCents: number,
  currency: string,
): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export function formatSubscriptionCadence(
  intervalCount: number,
  intervalUnit: "week" | "month" | "year",
): string {
  return intervalCount === 1
    ? `Every ${intervalUnit}`
    : `Every ${intervalCount} ${intervalUnit}s`;
}

export function formatServicePeriod(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string {
  if (!startsAt || !endsAt) return "Not available";
  return `${formatSubscriptionDate(startsAt)} – ${formatSubscriptionDate(endsAt)}`;
}

function statusPresentation(
  provider: ExpertProviderSubscription | null,
  localStatus: string | null,
): Pick<
  ExpertSubscriptionDisplayState,
  "statusLabel" | "statusTone" | "statusMessage"
> {
  if (!provider) {
    if (localStatus === "active") {
      return {
        statusLabel: "Active",
        statusTone: "success",
        statusMessage: null,
      };
    }
    return {
      statusLabel: localStatus
        ? localStatus.split("_").join(" ")
        : "No subscription",
      statusTone: "neutral",
      statusMessage: null,
    };
  }

  if (
    provider.cancel_at_period_end &&
    manageableStatuses.has(provider.status)
  ) {
    return {
      statusLabel: "Cancellation scheduled",
      statusTone: "warning",
      statusMessage:
        "Your subscription remains active until the end of the current billing period.",
    };
  }

  switch (provider.status) {
    case "trialing":
      return { statusLabel: "Trial", statusTone: "info", statusMessage: null };
    case "active":
      return {
        statusLabel: "Active",
        statusTone: "success",
        statusMessage: null,
      };
    case "past_due":
      return {
        statusLabel: "Payment past due",
        statusTone: "warning",
        statusMessage:
          "Update your payment method in Stripe to keep your subscription active.",
      };
    case "unpaid":
      return {
        statusLabel: "Payment required",
        statusTone: "warning",
        statusMessage:
          "Stripe could not collect payment. Open billing management to resolve it.",
      };
    case "paused":
      return {
        statusLabel: "Paused",
        statusTone: "warning",
        statusMessage:
          "Your subscription is paused. Manage it in Stripe or contact ShopExperts for help.",
      };
    case "checkout_pending":
    case "incomplete":
      return {
        statusLabel: "Setup incomplete",
        statusTone: "info",
        statusMessage:
          "Finish the open Stripe checkout to activate this subscription.",
      };
    case "incomplete_expired":
      return {
        statusLabel: "Checkout expired",
        statusTone: "neutral",
        statusMessage:
          "This checkout expired before the subscription was activated.",
      };
    case "canceled":
      return {
        statusLabel: "Canceled",
        statusTone: "neutral",
        statusMessage: "This Stripe subscription is no longer active.",
      };
  }
}

export function isPaymentProjectionComplete(
  overview: ExpertSubscriptionOverview | null | undefined,
): boolean {
  const provider = overview?.provider_subscription;
  const current = overview?.current_subscription;

  if (
    current &&
    !current.is_managed &&
    provider?.status === "trialing" &&
    provider.trial_ends_at
  ) {
    return true;
  }

  return Boolean(
    current?.is_managed &&
    provider &&
    !["checkout_pending", "incomplete", "incomplete_expired"].includes(
      provider.status,
    ),
  );
}

export function getExpertSubscriptionDisplayState(
  overview: ExpertSubscriptionOverview | null | undefined,
): ExpertSubscriptionDisplayState {
  const current = overview?.current_subscription ?? null;
  // A historical/manual subscription can coexist with provider-shaped data
  // from an unfinished or older Checkout. The current subscription's managed
  // marker is authoritative for whether Stripe controls the active plan.
  const provider = current
    ? current.is_managed
      ? (overview?.provider_subscription ??
        current.provider_subscription ??
        null)
      : null
    : (overview?.provider_subscription ?? null);
  const scheduledProvider =
    current &&
    !current.is_managed &&
    overview?.provider_subscription?.status === "trialing" &&
    overview.provider_subscription.trial_ends_at
      ? overview.provider_subscription
      : null;
  const option = provider?.billing_option ?? null;
  const latestInvoice = current?.recent_invoices?.[0] ?? null;
  const latestPaidInvoice =
    current?.recent_invoices?.find(
      (invoice: ExpertSubscriptionInvoice) => invoice.status === "paid",
    ) ?? null;
  const status = scheduledProvider
    ? {
        statusLabel: "Plan change scheduled",
        statusTone: "info" as const,
        statusMessage: null,
      }
    : statusPresentation(provider, current?.status ?? null);
  const cancelScheduled = Boolean(provider?.cancel_at_period_end);
  const paymentNeedsAttention = Boolean(
    provider && ["past_due", "unpaid", "paused"].includes(provider.status),
  );
  const displayedInvoice = paymentNeedsAttention
    ? latestPaidInvoice
    : latestInvoice;
  const dateDisplay = cancelScheduled
    ? { label: "Access until", source: provider?.current_period_ends_at }
    : paymentNeedsAttention
      ? {
          label: "Paid through",
          source:
            latestPaidInvoice?.service_period_ends_at ??
            current?.ends_at ??
            current?.renews_at,
        }
      : provider?.status === "trialing"
        ? { label: "Trial ends", source: provider.trial_ends_at }
        : provider
          ? {
              label: "Next renewal",
              source:
                provider.current_period_ends_at ??
                current?.renews_at ??
                current?.ends_at,
            }
          : {
              label: "Paid through",
              source: current?.renews_at ?? current?.ends_at,
            };
  const scheduledOption = scheduledProvider?.billing_option ?? null;

  return {
    planName:
      provider?.plan?.name ?? current?.plan?.name ?? "No active subscription",
    optionLabel: option?.label ?? null,
    ...status,
    amount: option
      ? formatSubscriptionAmount(option.amount_cents, option.currency)
      : null,
    cadence: option
      ? formatSubscriptionCadence(option.interval_count, option.interval_unit)
      : null,
    dateLabel: dateDisplay.label,
    dateValue: formatSubscriptionDate(dateDisplay.source),
    servicePeriod: formatServicePeriod(
      displayedInvoice?.service_period_starts_at ??
        provider?.current_period_starts_at,
      displayedInvoice?.service_period_ends_at ??
        provider?.current_period_ends_at,
    ),
    canManage: Boolean(provider && manageableStatuses.has(provider.status)),
    scheduledTransition: scheduledProvider
      ? {
          nextPlanName: scheduledProvider.plan?.name ?? "Stripe subscription",
          optionLabel: scheduledOption?.label ?? null,
          amount: scheduledOption
            ? formatSubscriptionAmount(
                scheduledOption.amount_cents,
                scheduledOption.currency,
              )
            : null,
          cadence: scheduledOption
            ? formatSubscriptionCadence(
                scheduledOption.interval_count,
                scheduledOption.interval_unit,
              )
            : null,
          startsAt: formatSubscriptionDate(scheduledProvider.trial_ends_at),
        }
      : null,
  };
}

export function portalErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "Stripe billing management could not be opened. Please try again.";
  }
  const response = (error as { response?: { data?: unknown } }).response;
  const body = response?.data;
  if (body && typeof body === "object") {
    const code = (body as { error_code?: unknown }).error_code;
    if (code === "billing_portal_unavailable") {
      return "Billing management is unavailable until your Stripe subscription is active.";
    }
  }
  return "Stripe billing management could not be opened. Please try again.";
}
